import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageMetadata from '../hooks/usePageMetadata';
import { STORE } from '../config/store';
import { containerWide, heroPageHeading, sectionSpacing } from '../styles/ui';

// Set explicitly, not generated. Update this whenever the policy text changes.
const LAST_UPDATED = '17 August 2026';

const linkClass =
  'text-emerald-800 underline underline-offset-4 hover:text-emerald-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 rounded';

// Local to this page: legal copy reads better as prose than as a data array,
// and this keeps every section's heading level and spacing identical.
const PolicySection = ({ id, title, children }) => (
  <section id={id} aria-labelledby={`${id}-heading`}>
    <h2 id={`${id}-heading`} className="serif text-2xl md:text-3xl text-emerald-950 mb-4">
      {title}
    </h2>
    <div className="text-gray-700 leading-relaxed space-y-4">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  usePageMetadata({
    title: 'Privacy Policy | Fresh & Homely',
    description:
      'How Fresh & Homely handles information in connection with its website. The site has no accounts, forms, online ordering or tracking — read what this means for your privacy.',
    canonicalPath: '/privacy-policy',
  });

  return (
    <>
      <Navbar />

      <main>
        <section className="hero-gradient pt-32 pb-14">
          <div className={containerWide}>
            <div className="max-w-4xl">
              <h1 className={`${heroPageHeading} mb-6`}>
                Privacy Policy
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                This Privacy Policy explains how Fresh &amp; Homely handles information in
                connection with this website. It describes how the website works today — it is a
                simple informational site for our store, without accounts, online ordering or
                tracking — and we will update it if that changes.
              </p>
              <p className="mt-6 text-sm font-medium text-emerald-900">
                Last updated: {LAST_UPDATED}
              </p>
            </div>
          </div>
        </section>

        <section className={`${sectionSpacing} bg-gradient-to-b from-white to-canvas`}>
          <div className={containerWide}>
            <div className="max-w-4xl space-y-12">
              <PolicySection id="information-we-collect" title="1. Information We Collect">
                <p>
                  This website is an informational website for our store. It does not offer account
                  registration or sign-in, online ordering or checkout, payment processing, or any
                  form or field for submitting your details. Because those features do not exist,
                  the website itself does not ask you for, or gather, information such as your name,
                  contact details or payment information.
                </p>
                <p>
                  This is a narrow statement about the website application. Delivering any website
                  still involves standard technical information being processed by the providers who
                  serve it, which is described in section 3.
                </p>
              </PolicySection>

              <PolicySection id="information-you-provide" title="2. Information You Provide Directly">
                <p>
                  Since the website has no forms, anything you share with us reaches us through
                  channels outside this website — by calling the phone number published on the site,
                  or by visiting the store in person.
                </p>
                <p>When you contact us that way, you may choose to tell us things such as:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>your name</li>
                  <li>a phone number or other contact detail you offer</li>
                  <li>details of your enquiry</li>
                  <li>the products or quantities you are looking for</li>
                  <li>wholesale or bulk supply requirements for your business</li>
                </ul>
                <p>
                  You provide this voluntarily and only if you choose to. We ask for no more than
                  what we need in order to answer you.
                </p>
              </PolicySection>

              <PolicySection id="technical-information" title="3. Automatically Processed Technical Information">
                <p>
                  As with any website, showing you these pages requires our hosting and
                  content-delivery providers to process standard technical information sent by your
                  browser. This can include your IP address, browser and device information, which
                  page was requested, the date and time, and diagnostic or security information.
                </p>
                <p>
                  This processing is part of delivering the website and keeping it available and
                  secure. The website does not operate any analytics service, advertising tool or
                  visitor-tracking system of its own, and it maintains no database in which visitor
                  records are assembled.
                </p>
              </PolicySection>

              <PolicySection id="cookies" title="4. Cookies and Similar Technologies">
                <p>
                  This website does not set cookies of its own, and it does not use advertising or
                  analytics cookies. It also does not store information in your browser's local
                  storage or session storage.
                </p>
                <p>
                  The website does load one type of third-party resource — the fonts described in
                  section 5. Those third parties decide their own use of cookies and similar
                  technologies, under their own privacy practices.
                </p>
                <p>
                  If we later add functionality that requires cookies or similar technologies, we
                  will update this policy and, where required, ask for consent.
                </p>
              </PolicySection>

              <PolicySection id="third-party-services" title="5. Third-Party Services">
                <p>The website relies on a small number of external services:</p>
                <ul className="list-disc pl-6 space-y-3">
                  <li>
                    <span className="font-semibold text-emerald-950">Google Fonts.</span> The
                    typefaces used on this site are loaded from Google's font servers. Your browser
                    requests those font files directly from Google, which means Google receives that
                    request, including your IP address and browser information, and handles it under
                    its own privacy practices.
                  </li>
                  <li>
                    <span className="font-semibold text-emerald-950">Google Maps — as a link
                    only.</span> The site offers a link that opens our store location in Google
                    Maps. Google Maps is not embedded in these pages, and no map content or Google
                    Maps code loads while you browse this website. Google is only contacted if you
                    choose to open that link, at which point you are using Google's own service
                    under its terms and privacy practices.
                  </li>
                  <li>
                    <span className="font-semibold text-emerald-950">Website hosting and
                    delivery.</span> The site is published as a static website on Amazon Web
                    Services infrastructure, which serves the pages to your browser and processes
                    the technical request information described in section 3.
                  </li>
                </ul>
                <p>
                  Each of these providers processes information according to its own privacy
                  practices, which we do not control.
                </p>
              </PolicySection>

              <PolicySection id="how-information-is-used" title="6. How Information May Be Used">
                <p>
                  Where you have given us information by contacting the store, we use it for the
                  purpose you got in touch about, and for reasonable follow-up on it. In practice
                  that means:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>responding to your enquiry</li>
                  <li>discussing product availability, pricing or a specific requirement</li>
                  <li>responding to wholesale and bulk supply enquiries</li>
                  <li>helping with questions about our location or visiting the store</li>
                  <li>general customer support</li>
                  <li>keeping the website available, reliable and secure</li>
                </ul>
                <p>
                  We do not use this information for automated profiling, personalised advertising or
                  building marketing lists, and the website has no functionality that would do so.
                </p>
              </PolicySection>

              <PolicySection id="sharing" title="7. Sharing of Information">
                <p>
                  Operating the website means some information is necessarily handled by the service
                  providers named in section 5 — the hosting and content-delivery provider that
                  serves the pages, and the font provider your browser requests files from.
                </p>
                <p>
                  Beyond that, we do not share the details of your enquiries with third parties for
                  their own marketing purposes. Information may be disclosed where we are required to
                  do so by law, or where it is necessary to establish or defend legal rights or to
                  protect the safety of people or property.
                </p>
              </PolicySection>

              <PolicySection id="retention" title="8. Data Retention">
                <p>
                  The website has no customer database, so it does not build or keep visitor records
                  of its own.
                </p>
                <p>
                  Where you have contacted the store directly, we keep what you told us only for as
                  long as is reasonably necessary for that enquiry or the related business purpose,
                  and for any period we are required to keep records for legal or accounting reasons.
                </p>
                <p>
                  Technical information processed by our hosting and delivery providers is retained
                  by them in line with their own practices and retention settings.
                </p>
              </PolicySection>

              <PolicySection id="security" title="9. Data Security">
                <p>
                  The website is served to your browser over an encrypted (HTTPS) connection.
                  Because it offers no accounts, payments or forms, it does not hold customer
                  records or payment details.
                </p>
                <p>
                  We take reasonable care with information shared with us. That said, no method of
                  transmitting information over the internet or storing it electronically can be
                  guaranteed to be completely secure, and we cannot promise absolute security.
                </p>
              </PolicySection>

              <PolicySection id="childrens-privacy" title="10. Children's Privacy">
                <p>
                  This is a general business website for our store. It is not directed at children
                  and offers no accounts, forms or other features intended for them, so it does not
                  knowingly collect personal information from children through the website.
                </p>
                <p>
                  If you are a parent or guardian and believe a child has given us information by
                  contacting the store directly, please call us using the details in section 14 and
                  we will address it.
                </p>
              </PolicySection>

              <PolicySection id="your-rights" title="11. Your Privacy Choices and Rights">
                <p>
                  Depending on where you live and the laws that apply to you, you may have rights
                  concerning personal information — for example, asking what information we hold
                  about you, asking us to correct it, or asking us to delete it.
                </p>
                <p>
                  Because this website does not collect or store personal information, such requests
                  will normally concern information you shared with the store directly. If you have
                  a privacy question or want to make a request, please contact us using the details
                  in section 14 and we will respond as appropriate under the applicable law.
                </p>
              </PolicySection>

              <PolicySection id="external-links" title="12. External Links">
                <p>
                  This website links to external services, such as Google Maps for directions to the
                  store. When you follow a link like that, you leave this website and continue on a
                  service we do not operate or control.
                </p>
                <p>
                  We are not responsible for the content or the privacy practices of external
                  websites. We would encourage you to read their privacy policies where those
                  practices matter to you.
                </p>
              </PolicySection>

              <PolicySection id="changes" title="13. Changes to This Privacy Policy">
                <p>
                  We may update this policy when the website's functionality or our privacy
                  practices change — for example, if we introduce a contact form, online ordering or
                  any form of analytics in the future.
                </p>
                <p>
                  When we do, the revised policy will be published on this page with a new date.
                  Please check the "Last updated" date at the top to see when it last changed.
                </p>
              </PolicySection>

              <PolicySection id="contact-us" title="14. Contact Us">
                <p>
                  If you have questions about this Privacy Policy or about information you have
                  shared with us, please get in touch:
                </p>
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 space-y-3 shadow-soft">
                  <p className="font-semibold text-emerald-950">{STORE.name}</p>
                  <p>
                    <span className="font-medium text-emerald-950">Phone: </span>
                    <a href={STORE.phoneHref} className={linkClass}>
                      {STORE.phone}
                    </a>
                  </p>
                  <div>
                    <span className="font-medium text-emerald-950">Address: </span>
                    <address className="not-italic inline">{STORE.address}</address>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Phone and visiting the store in person are the contact methods we publish for
                  Fresh &amp; Homely. If we add other channels later, we will list them here.
                </p>
              </PolicySection>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;
